import { CalendarCheck } from "lucide-react";
import type { RecurringException } from "@opus-dt/shared-types";
import { usePreviewOccurrences } from "./hooks";

interface Props {
  rule: RecurringException;
}

const fmt = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  timeZone: "UTC",
});

// As PRÓXIMAS 6 OCORRÊNCIAS, resolvidas PELO SERVIDOR.
// A interface nunca interpreta regra de calendário. Se ela calculasse "a primeira segunda de
// setembro", passaria a existir uma TERCEIRA implementação da expansão de recorrência — e ela seria
// a única que o gestor vê antes de salvar. Divergir dela é divergir exatamente no ponto onde a
// confiança se forma: o gestor confere as datas, aprova, e o indicador usa outras.
// A prévia existe porque "4ª quinta do mês" é fácil de escrever e difícil de conferir de cabeça.
// Ver 24/09, 22/10, 26/11 é o que transforma a regra em algo que o gestor pode aprovar.
export function OccurrencePreview({ rule }: Props) {
  // A assinatura ignora o MOTIVO de propósito: ele não afeta quando a regra incide, e incluí-lo
  // dispararia uma requisição por caractere digitado para receber exatamente as mesmas datas.
  const signature = JSON.stringify({
    freq: rule.freq,
    weekday: "weekday" in rule ? rule.weekday : null,
    ordinal: "ordinal" in rule ? rule.ordinal : null,
    month: "month" in rule ? rule.month : null,
    day: "day" in rule ? rule.day : null,
  });
  const preview = usePreviewOccurrences({ rule, limit: 6 }, signature);
  const occurrences = preview.data?.occurrences;

  // Erro fica NO CARD DA REGRA, não num toast global: ele pertence à regra que o gestor está
  // editando, e um toast o tiraria de perto do controle que o causou.
  if (preview.isError) {
    return (
      <p className="text-[11px] text-rd-warning-700" role="status">
        Não foi possível calcular as próximas ocorrências. A regra ainda pode ser salva.
      </p>
    );
  }
  if (!occurrences) {
    return <p className="text-[11px] text-text-tertiary">Calculando próximas ocorrências…</p>;
  }
  if (occurrences.length === 0) {
    return (
      <p className="text-[11px] text-text-tertiary" role="status">
        Esta regra não tem ocorrência no horizonte consultado.
      </p>
    );
  }

  return (
    <p
      className="flex flex-wrap items-center gap-1.5 text-[11px] text-text-secondary"
      data-testid="occurrence-preview"
      role="status"
    >
      <CalendarCheck className="h-3 w-3 shrink-0 text-text-tertiary" aria-hidden />
      <span className="text-text-tertiary">Próximas:</span>
      {occurrences.map((d) => (
        <span key={d} className="font-mono">
          {fmt.format(new Date(`${d}T12:00:00Z`))}
        </span>
      ))}
    </p>
  );
}
